import { MenuBar } from "@/components/common/MenuBar";
import { loyaltyTierLabel, TIER_COLORS } from "@/components/loyalty-badges";
import { ThemedText } from "@/components/themed-text";
import { BrandButton } from "@/components/ui/brand-button";
import { Loader } from "@/components/ui/loader";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { getApiErrorMessage } from "@/lib/api";
import reputationService, {
  BadgeStatus,
  TrustBadgeKey,
} from "@/lib/services/reputation";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { RefreshControl, ScrollView, StyleSheet, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

type IoniconName = React.ComponentProps<typeof Ionicons>["name"];

const BADGE_DETAILS: Record<TrustBadgeKey, { title: string; icon: IoniconName }> =
  {
    verified_operator: {
      title: "Verified Operator",
      icon: "shield-checkmark-outline",
    },
    experienced: { title: "Experienced", icon: "ribbon-outline" },
    reliable: { title: "Reliable", icon: "speedometer-outline" },
    top_rated: { title: "Top Rated", icon: "star-outline" },
  };

const BADGE_STATUS: Record<BadgeStatus, { label: string; color: string }> = {
  earned: { label: "Earned", color: BrandColors.primary },
  in_progress: { label: "In progress", color: "#D97706" },
  locked: { label: "Locked", color: BrandColors.mediumGray },
};

const formatPercent = (value: number) =>
  `${Number.isInteger(value) ? value : value.toFixed(1)}%`;

const formatTierRange = (min: number, max: number | null) =>
  max === null
    ? `${min.toLocaleString()}+ pts`
    : `${min.toLocaleString()}–${max.toLocaleString()} pts`;

const formatReviewDate = (date: string) =>
  new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

function ProgressBar({
  percent,
  color,
  trackColor,
}: {
  percent: number;
  color: string;
  trackColor: string;
}) {
  return (
    <View style={[styles.progressTrack, { backgroundColor: trackColor }]}>
      <View
        style={[
          styles.progressFill,
          { width: `${percent}%`, backgroundColor: color },
        ]}
      />
    </View>
  );
}

export default function RewardsScreen() {
  const { theme } = useAuth();
  const desktop = useDesktopLayout();
  const {
    data,
    isLoading,
    isRefetching,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: ["reputation"],
    queryFn: () => reputationService.getSummary(),
  });

  const error = loadError
    ? getApiErrorMessage(loadError, "Failed to load reputation")
    : null;
  const cardStyle = [
    styles.card,
    { backgroundColor: theme.background, borderColor: theme.border },
  ];

  const renderContent = () => {
    if (!data) {
      return null;
    }

    const { reputation, stats, loyalty, badges, recent_reviews } = data;
    const tierColors = TIER_COLORS[loyalty.tier] ?? TIER_COLORS.bronze;
    const statItems = [
      { label: "Completed jobs", value: stats.completed_jobs.toLocaleString() },
      {
        label: "Verified reviews",
        value: stats.verified_reviews.toLocaleString(),
      },
      { label: "On-time rate", value: formatPercent(stats.on_time_rate) },
      {
        label: "Cancellation rate",
        value: formatPercent(stats.cancellation_rate),
      },
    ];

    return (
      <>
        <View style={cardStyle}>
          <View style={styles.cardHeaderRow}>
            <ThemedText type="defaultSemiBold">Your Reputation</ThemedText>
            {reputation.is_verified ? (
              <View style={styles.inlineRow}>
                <Ionicons
                  name="checkmark-circle-outline"
                  size={14}
                  color={BrandColors.primary}
                />
                <ThemedText style={styles.primaryText} type="extraSmall">
                  Verified operator
                </ThemedText>
              </View>
            ) : null}
          </View>
          <ThemedText style={styles.mutedText} type="extraSmall">
            Built from verified customer performance
          </ThemedText>

          <View style={[styles.inlineRow, styles.ratingRow]}>
            <Ionicons name="star" size={16} color={BrandColors.warning} />
            <ThemedText type="defaultSemiBold">
              {reputation.score.toFixed(1)}
            </ThemedText>
            <ThemedText style={styles.mutedText} type="extraSmall">
              {reputation.total_ratings_count} reviews
            </ThemedText>
          </View>

          <View style={[styles.statsGrid, { borderColor: theme.border }]}>
            {statItems.map((item, index) => (
              <View
                key={item.label}
                style={[
                  styles.statCell,
                  { borderColor: theme.border },
                  index % 2 === 0 && styles.statCellLeft,
                  index < 2 && styles.statCellTop,
                ]}
              >
                <ThemedText style={styles.statValue}>{item.value}</ThemedText>
                <ThemedText style={styles.mutedText} type="extraSmall">
                  {item.label}
                </ThemedText>
              </View>
            ))}
          </View>
        </View>

        <View style={cardStyle}>
          <ThemedText type="defaultSemiBold">Loyalty & Rewards</ThemedText>
          <ThemedText style={styles.mutedText} type="extraSmall">
            Earn points from completed work and referrals
          </ThemedText>

          <View
            style={[
              styles.tierCard,
              {
                backgroundColor: tierColors.background,
                borderColor: `${tierColors.text}40`,
              },
            ]}
          >
            <View style={styles.tierHeader}>
              <View style={styles.tierIcon}>
                <Ionicons
                  name="star-outline"
                  size={18}
                  color={tierColors.text}
                />
              </View>
              <View>
                <ThemedText
                  type="extraSmall"
                  lightColor={BrandColors.darkGray}
                  darkColor={BrandColors.darkGray}
                >
                  Current tier
                </ThemedText>
                <ThemedText style={[styles.tierName, { color: tierColors.text }]}>
                  {loyaltyTierLabel(loyalty.tier)}
                </ThemedText>
                <ThemedText
                  type="extraSmall"
                  lightColor={BrandColors.darkGray}
                  darkColor={BrandColors.darkGray}
                >
                  {loyalty.points.toLocaleString()} loyalty points
                </ThemedText>
              </View>
            </View>

            <View style={styles.tierProgressHeader}>
              <ThemedText
                type="smallSemiBold"
                lightColor={BrandColors.charcoal}
                darkColor={BrandColors.charcoal}
              >
                {loyalty.next_tier
                  ? `${loyalty.points_to_next_tier.toLocaleString()} points to ${loyaltyTierLabel(loyalty.next_tier)}`
                  : "Highest tier reached"}
              </ThemedText>
              <ThemedText
                style={{ color: tierColors.text }}
                type="smallSemiBold"
              >
                {loyalty.progress_percent}%
              </ThemedText>
            </View>
            <ProgressBar
              percent={loyalty.progress_percent}
              color={tierColors.text}
              trackColor={BrandColors.white}
            />
            {loyalty.next_tier && loyalty.next_tier_points !== null ? (
              <View style={styles.tierProgressLabels}>
                <ThemedText
                  type="extraSmall"
                  lightColor={BrandColors.darkGray}
                  darkColor={BrandColors.darkGray}
                >
                  {loyaltyTierLabel(loyalty.tier)}
                </ThemedText>
                <ThemedText
                  type="extraSmall"
                  lightColor={BrandColors.darkGray}
                  darkColor={BrandColors.darkGray}
                >
                  {loyaltyTierLabel(loyalty.next_tier)}{" "}
                  {loyalty.next_tier_points.toLocaleString()}
                </ThemedText>
              </View>
            ) : null}
          </View>

          <View style={styles.infoRow}>
            <View style={styles.infoIcon}>
              <Ionicons
                name="wallet-outline"
                size={16}
                color={BrandColors.warning}
              />
            </View>
            <View style={styles.flex}>
              <ThemedText type="smallSemiBold">How points work</ThemedText>
              <ThemedText style={styles.mutedText} type="extraSmall">
                1 point per £1 of completed bookings, plus referral bonuses.
              </ThemedText>
            </View>
          </View>

          <View style={styles.tierRanges}>
            {loyalty.tiers.map((range) => {
              const colors = TIER_COLORS[range.tier] ?? TIER_COLORS.bronze;

              return (
                <View
                  key={range.tier}
                  style={[
                    styles.tierRange,
                  ]}
                >
                  <ThemedText
                    style={{ color: colors.text, backgroundColor: colors.background, paddingVertical: 3 , paddingHorizontal: 9, borderRadius: 10, marginBottom: 4 }}
                    type="extraSmallSemiBold"
                  >
                    {loyaltyTierLabel(range.tier)}
                  </ThemedText>
                  <ThemedText
                    style={[styles.tierRangeText, {fontSize: 10}]}
                    type="extraSmall"
                    lightColor={BrandColors.darkGray}
                    darkColor={BrandColors.darkGray}
                  >
                    {formatTierRange(range.min_points, range.max_points)}
                  </ThemedText>
                </View>
              );
            })}
          </View>

          <View style={styles.infoRow}>
            <Ionicons
              name="checkmark-circle-outline"
              size={16}
              color={BrandColors.primary}
            />
            <View style={styles.flex}>
              <ThemedText type="extraSmallSemiBold">
                Loyalty tiers and trust badges are separate.
              </ThemedText>
              <ThemedText style={styles.mutedText} type="extraSmall">
                Points measure your activity and appear on the leaderboard.
                Trust badges reflect verified performance. Top Rated is based
                on customer ratings, not points.
              </ThemedText>
            </View>
          </View>
        </View>

        <View style={[cardStyle, styles.listCard]}>
          <View style={styles.listCardHeader}>
            <ThemedText type="defaultSemiBold">Trust Badges</ThemedText>
            <ThemedText style={styles.mutedText} type="extraSmall">
              Earned from verified performance, not loyalty points
            </ThemedText>
          </View>
          {badges.map((badge) => {
            const details = BADGE_DETAILS[badge.key];
            const status = BADGE_STATUS[badge.status];
            const earned = badge.status === "earned";

            return (
              <View
                key={badge.key}
                style={[styles.badgeRow, { borderTopColor: theme.border }]}
              >
                <View
                  style={[
                    styles.badgeIcon,
                    {
                      borderColor: earned ? BrandColors.charcoal : theme.border,
                    },
                  ]}
                >
                  <Ionicons
                    name={details.icon}
                    size={20}
                    color={earned ? theme.text : BrandColors.mediumGray}
                  />
                  {earned ? <View style={styles.badgeEarnedDot} /> : null}
                </View>
                <View style={styles.flex}>
                  <ThemedText type="smallSemiBold">{details.title}</ThemedText>
                  <ThemedText
                    style={{ color: status.color }}
                    type="extraSmallSemiBold"
                  >
                    {status.label}
                  </ThemedText>
                  {badge.status === "in_progress" ? (
                    <View style={styles.badgeProgress}>
                      <ProgressBar
                        percent={badge.progress_percent}
                        color={BrandColors.primary}
                        trackColor={theme.border}
                      />
                    </View>
                  ) : null}
                  <ThemedText style={styles.mutedText} type="extraSmall">
                    {badge.detail}
                  </ThemedText>
                </View>
              </View>
            );
          })}
        </View>

        <View style={[cardStyle, styles.listCard]}>
          <View style={styles.listCardHeader}>
            <ThemedText type="defaultSemiBold">Recent reviews</ThemedText>
            <ThemedText style={styles.mutedText} type="extraSmall">
              Feedback from customers on completed bookings
            </ThemedText>
          </View>
          {recent_reviews.length === 0 ? (
            <View style={[styles.reviewRow, { borderTopColor: theme.border }]}>
              <ThemedText style={styles.mutedText} type="small">
                No reviews yet. Reviews appear here after customers rate
                completed bookings.
              </ThemedText>
            </View>
          ) : (
            recent_reviews.map((review) => (
              <View
                key={review.id}
                style={[styles.reviewRow, { borderTopColor: theme.border }]}
              >
                <View style={styles.cardHeaderRow}>
                  <View style={styles.inlineRow}>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Ionicons
                        key={index}
                        name={index < review.rating ? "star" : "star-outline"}
                        size={14}
                        color={BrandColors.warning}
                      />
                    ))}
                  </View>
                  <ThemedText style={styles.mutedText} type="extraSmall">
                    {formatReviewDate(review.created_at)}
                  </ThemedText>
                </View>
                {review.is_verified ? (
                  <View style={styles.verifiedChip}>
                    <Ionicons name="checkmark" size={10} color="#16A34A" />
                    <ThemedText
                      style={styles.verifiedChipText}
                      type="extraSmall"
                    >
                      Verified booking
                    </ThemedText>
                  </View>
                ) : null}
                {review.comment ? (
                  <ThemedText type="small">{review.comment}</ThemedText>
                ) : null}
              </View>
            ))
          )}
        </View>

        <ThemedText style={[styles.mutedText, styles.footnote]} type="extraSmall">
          Reputation and trust badges are based on verified activity. Loyalty
          points are separate and do not affect your badges.
        </ThemedText>
      </>
    );
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["top"]}
    >
      {isLoading ? (
        <Loader inline />
      ) : error ? (
        <View style={styles.centerContent}>
          <ThemedText style={styles.errorText} type="small">
            {error}
          </ThemedText>
          <BrandButton onPress={() => refetch()} style={styles.retry}>
            Retry
          </BrandButton>
        </View>
      ) : (
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          refreshControl={
            <RefreshControl
              refreshing={isRefetching}
              onRefresh={() => refetch()}
            />
          }
        >
          {!desktop ? <MenuBar style={styles.menuBar} /> : null}
          <View style={styles.pageHeader}>
            <ThemedText type="subtitle">Reputation & Rewards</ThemedText>
            <ThemedText style={styles.mutedText} type="extraSmall">
              Your performance, recognition and loyalty progress
            </ThemedText>
          </View>
          {renderContent()}
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContent: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  errorText: {
    textAlign: "center",
    color: BrandColors.mediumGray,
  },
  retry: {
    marginTop: 16,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
    gap: 16,
    width: "100%",
    maxWidth: 720,
    alignSelf: "center",
  },
  menuBar: {
    position: "relative",
    top: 0,
    left: 0,
  },
  pageHeader: {
    gap: 2,
  },
  flex: {
    flex: 1,
  },
  mutedText: {
    color: BrandColors.mediumGray,
  },
  primaryText: {
    color: BrandColors.primary,
  },
  card: {
    borderWidth: 1,
    borderRadius: 16,
    padding: 16,
    gap: 4,
  },
  listCard: {
    paddingHorizontal: 0,
    paddingBottom: 0,
  },
  listCardHeader: {
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  cardHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  inlineRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  ratingRow: {
    marginTop: 8,
    gap: 6,
  },
  statsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 12,
    borderTopWidth: 1,
  },
  statCell: {
    width: "50%",
    alignItems: "center",
    paddingVertical: 14,
  },
  statCellLeft: {
    borderRightWidth: 1,
  },
  statCellTop: {
    borderBottomWidth: 1,
  },
  statValue: {
    fontSize: 22,
    lineHeight: 28,
    fontFamily: "PlusJakartaSansBold",
  },
  tierCard: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    marginTop: 12,
    gap: 8,
  },
  tierHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
  },
  tierIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: BrandColors.white,
    alignItems: "center",
    justifyContent: "center",
  },
  tierName: {
    fontSize: 22,
    lineHeight: 28,
    fontFamily: "PlusJakartaSansBold",
  },
  tierProgressHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  tierProgressLabels: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  progressTrack: {
    height: 6,
    borderRadius: 3,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 3,
  },
  infoRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    marginTop: 16,
  },
  infoIcon: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: "#FEF3C7",
    alignItems: "center",
    justifyContent: "center",
  },
  tierRanges: {
    flexDirection: "row",
    gap: 8,
    marginTop: 12,
    backgroundColor: "#F8FAFC",
    padding: 10,
    borderRadius: 12,
  },
  tierRange: {
    flex: 1,
    alignItems: "center",
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  tierRangeText: {
    textAlign: "center",
  },
  badgeRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 14,
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderTopWidth: 1,
  },
  badgeIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 1.5,
    alignItems: "center",
    justifyContent: "center",
  },
  badgeEarnedDot: {
    position: "absolute",
    right: -2,
    bottom: -2,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: BrandColors.success,
    borderWidth: 2,
    borderColor: BrandColors.white,
  },
  badgeProgress: {
    marginVertical: 6,
  },
  reviewRow: {
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderTopWidth: 1,
    gap: 6,
  },
  verifiedChip: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 4,
    borderRadius: 20,
    paddingHorizontal: 8,
    paddingVertical: 2,
    backgroundColor: "#DCFCE7",
  },
  verifiedChipText: {
    color: "#16A34A",
  },
  footnote: {
    paddingHorizontal: 4,
  },
});
